if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
      }

      hide() {
        this.stopTouchZoom();
        this.zoomResize?.abort();
        this.removeTouchThumbnails();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        const activeMedia = this.showActiveMedia();
        this.zoomResize?.abort();
        if (activeMedia?.tagName === 'IMG') {
          this.zoomResize = new AbortController();
          window.addEventListener('resize', () => {
            if (this.touchZoom || window.matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse)').matches)
              this.showActiveMedia(this.selectedMediaId, true);
          }, { signal: this.zoomResize.signal });
        }
      }

      disconnectedCallback() {
        if (this.hasAttribute('open')) this.hide();
        this.stopTouchZoom();
        this.zoomResize?.abort();
        this.removeTouchThumbnails();
      }

      showActiveMedia(mediaId = this.openedBy.getAttribute('data-media-id'), fit = false) {
        const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return null;
        this.stopTouchZoom();
        this.selectedMediaId = mediaId;
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${mediaId}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        const container = this.querySelector('[role="document"]');
        const hadThumbnails = Boolean(this.zoomThumbnails);
        this.prepareTouchThumbnails(activeMedia);
        if (!this.startTouchZoom(activeMedia, container)) {
          this.removeTouchThumbnails();
          activeMedia.scrollIntoView();
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        } else if (fit) {
          this.fitTouchZoom();
        }
        if (hadThumbnails || this.zoomThumbnails) {
          const focus = this.zoomThumbnails?.contains(document.activeElement) ? document.activeElement : container;
          trapFocus(this, focus);
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
        return activeMedia;
      }

      prepareTouchThumbnails(activeMedia) {
        if (activeMedia.tagName !== 'IMG' || !window.PointerEvent ||
            !window.matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse)').matches) {
          this.removeTouchThumbnails();
          return;
        }
        if (!this.zoomThumbnails) {
          const template = this.querySelector('template[data-zoom-thumbnails]');
          if (!template) return;
          const rail = template.content.firstElementChild.cloneNode(true);
          const buttons = [...rail.querySelectorAll('[data-zoom-media-id]')];
          if (buttons.length < 2) return;
          this.zoomThumbnails = rail;
          this.zoomThumbnailEvents = new AbortController();
          const options = { signal: this.zoomThumbnailEvents.signal };
          // Native ModalDialog closes an image on mouse pointerup. The rail is a control, not that image.
          rail.addEventListener('pointerup', (event) => event.stopPropagation(), options);
          buttons.forEach((button, index) => {
            const select = () => {
              const id = button.getAttribute('data-zoom-media-id');
              if (id !== this.selectedMediaId) this.showActiveMedia(id, true);
            };
            button.addEventListener('click', select, options);
            button.addEventListener('keydown', (event) => {
              if (event.altKey || event.ctrlKey || event.metaKey) return;
              const next = event.key === 'ArrowRight' ? Math.min(index + 1, buttons.length - 1)
                : event.key === 'ArrowLeft' ? Math.max(index - 1, 0)
                : event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : null;
              if (next === null) return;
              event.preventDefault();
              buttons[next].focus({ preventScroll: true });
              buttons[next].click();
              buttons[next].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
            }, options);
          });
          this.querySelector('[role="dialog"]').append(rail);
          this.classList.add('has-touch-thumbnails');
        }
        this.zoomThumbnails.querySelectorAll('[data-zoom-media-id]').forEach((button) => {
          button.setAttribute('aria-current', String(button.getAttribute('data-zoom-media-id') === this.selectedMediaId));
        });
      }

      removeTouchThumbnails() {
        this.zoomThumbnailEvents?.abort();
        this.zoomThumbnails?.remove();
        this.zoomThumbnails = null;
        this.classList.remove('has-touch-thumbnails');
      }

      startTouchZoom(image, container) {
        if (image.tagName !== 'IMG' || !window.PointerEvent ||
            !window.matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse)').matches)
          return false;
        const width = image.offsetWidth;
        const height = image.offsetHeight;
        if (!(width > 0 && height > 0)) return false;

        this.classList.add('is-touch-zoom');
        container.scrollLeft = container.scrollTop = 0;
        const bounds = container.getBoundingClientRect();
        if (!(bounds.width > 0 && bounds.height > 0)) {
          this.classList.remove('is-touch-zoom');
          return false;
        }
        const minimum = Math.min(1, bounds.width / width, bounds.height / height);
        const state = this.touchZoom = {
          image, container, width, height, bounds, minimum,
          maximum: 1, scale: 1,
          x: (bounds.width - width) / 2, y: 0,
          pointers: new Map(), controller: new AbortController(), gesture: null, tap: null, lastTap: null,
        };
        container.style.setProperty('--media-zoom-width', `${width}px`);
        this.paintTouchZoom();
        const options = { signal: state.controller.signal };
        container.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'touch') return;
          if (state.pointers.size) {
            state.tap = state.lastTap = null;
          }
          if (state.pointers.size >= 2) return;
          if (!state.pointers.size) state.tap = {
            id: event.pointerId, x: event.clientX, y: event.clientY, time: event.timeStamp,
          };
          state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          try { container.setPointerCapture(event.pointerId); } catch (_) { /* End events also reach the document. */ }
          this.beginTouchGesture();
          event.preventDefault();
        }, options);
        container.addEventListener('pointermove', (event) => {
          if (!state.pointers.has(event.pointerId)) return;
          if (state.tap && Math.hypot(event.clientX - state.tap.x, event.clientY - state.tap.y) > 10)
            state.tap = state.lastTap = null;
          state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          const points = [...state.pointers.values()];
          const gesture = state.gesture;
          if (points.length === 2) {
            const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
            if (gesture.distance > 0) {
              state.scale = Math.max(state.minimum, Math.min(state.maximum, gesture.scale * distance / gesture.distance));
              state.x = (points[0].x + points[1].x) / 2 - bounds.left - gesture.anchorX * state.scale;
              state.y = (points[0].y + points[1].y) / 2 - bounds.top - gesture.anchorY * state.scale;
            } else {
              this.beginTouchGesture();
            }
          } else {
            state.x = gesture.x + points[0].x - gesture.point.x;
            state.y = gesture.y + points[0].y - gesture.point.y;
          }
          this.paintTouchZoom();
          event.preventDefault();
        }, options);
        const end = (event) => {
          const tap = state.tap;
          if (state.pointers.has(event.pointerId) && tap?.id === event.pointerId) {
            const elapsed = event.timeStamp - tap.time;
            const point = state.pointers.get(event.pointerId);
            const x = Number.isFinite(event.clientX) ? event.clientX : point.x;
            const y = Number.isFinite(event.clientY) ? event.clientY : point.y;
            if (event.type === 'pointerup' && elapsed >= 0 && elapsed <= 300 &&
                Math.hypot(x - tap.x, y - tap.y) <= 10) {
              const previous = state.lastTap;
              if (previous && event.timeStamp - previous.time <= 320 &&
                  Math.hypot(x - previous.x, y - previous.y) <= 24) {
                if (state.scale > state.minimum) this.fitTouchZoom();
                state.lastTap = null;
                event.preventDefault();
              } else state.lastTap = { x, y, time: event.timeStamp };
            } else state.lastTap = null;
            state.tap = null;
          }
          if (!state.pointers.delete(event.pointerId)) return;
          if (container.hasPointerCapture(event.pointerId)) container.releasePointerCapture(event.pointerId);
          this.beginTouchGesture();
        };
        document.addEventListener('pointerup', end, options);
        document.addEventListener('pointercancel', end, options);
        container.addEventListener('lostpointercapture', end, options);
        window.addEventListener('blur', () => this.clearTouchPointers(), options);
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.clearTouchPointers();
        }, options);
        image.addEventListener('load', () => {
          if (!(image.offsetHeight > 0)) return;
          state.height = image.offsetHeight;
          state.minimum = Math.min(1, bounds.width / width, bounds.height / state.height);
          state.scale = Math.max(state.minimum, Math.min(state.maximum, state.scale));
          this.paintTouchZoom();
          this.beginTouchGesture();
        }, { ...options, once: true });
        container.addEventListener('dragstart', (event) => event.preventDefault(), options);
        container.addEventListener('keydown', (event) => this.touchZoomKey(event), options);
        return true;
      }

      beginTouchGesture() {
        const state = this.touchZoom;
        if (!state) return;
        const points = [...state.pointers.values()];
        if (points.length === 2) {
          state.gesture = {
            scale: state.scale,
            distance: Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y),
            anchorX: ((points[0].x + points[1].x) / 2 - state.bounds.left - state.x) / state.scale,
            anchorY: ((points[0].y + points[1].y) / 2 - state.bounds.top - state.y) / state.scale,
          };
        } else {
          state.gesture = points.length ? { point: points[0], x: state.x, y: state.y } : null;
        }
      }

      paintTouchZoom() {
        const state = this.touchZoom;
        const clamp = (position, imageSize, viewportSize) => imageSize <= viewportSize
          ? (viewportSize - imageSize) / 2 : Math.min(0, Math.max(viewportSize - imageSize, position));
        state.x = clamp(state.x, state.width * state.scale, state.bounds.width);
        state.y = clamp(state.y, state.height * state.scale, state.bounds.height);
        state.container.style.setProperty('--media-zoom-transform', `translate(${state.x}px, ${state.y}px) scale(${state.scale})`);
      }

      fitTouchZoom() {
        const state = this.touchZoom;
        if (!state) return;
        state.scale = state.minimum;
        this.paintTouchZoom();
        this.beginTouchGesture();
      }

      touchZoomKey(event) {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const state = this.touchZoom;
        state.tap = state.lastTap = null;
        const steps = { ArrowLeft: [40, 0], ArrowRight: [-40, 0], ArrowUp: [0, 40], ArrowDown: [0, -40],
          PageUp: [0, state.bounds.height * 0.9], PageDown: [0, -state.bounds.height * 0.9],
          ' ': [0, state.bounds.height * 0.9 * (event.shiftKey ? 1 : -1)] };
        if (steps[event.key]) {
          state.x += steps[event.key][0];
          state.y += steps[event.key][1];
        } else if (event.key === 'Home' || event.key === 'End') {
          state.y = event.key === 'Home' ? 0 : state.bounds.height - state.height * state.scale;
        } else if (['+', '=', '-', '0'].includes(event.key)) {
          const scale = event.key === '0' ? state.minimum
            : Math.max(state.minimum, Math.min(state.maximum, state.scale * (event.key === '-' ? 0.8 : 1.25)));
          state.x = state.bounds.width / 2 - (state.bounds.width / 2 - state.x) * scale / state.scale;
          state.y = state.bounds.height / 2 - (state.bounds.height / 2 - state.y) * scale / state.scale;
          state.scale = scale;
        } else return;
        this.paintTouchZoom();
        this.beginTouchGesture();
        event.preventDefault();
      }

      clearTouchPointers() {
        const state = this.touchZoom;
        if (!state) return;
        const ids = [...state.pointers.keys()];
        state.pointers.clear();
        state.gesture = null;
        state.tap = state.lastTap = null;
        ids.forEach((id) => {
          if (state.container.hasPointerCapture(id)) state.container.releasePointerCapture(id);
        });
      }

      stopTouchZoom() {
        const state = this.touchZoom;
        if (state) {
          state.controller.abort();
          this.clearTouchPointers();
          state.container.style.removeProperty('--media-zoom-width');
          state.container.style.removeProperty('--media-zoom-transform');
        }
        this.touchZoom = null;
        this.classList.remove('is-touch-zoom');
      }
    }
  );
}
